import { fittingUpgradeTargetSchema } from "@frontier/content/ship-fitting";
import { shipSystemIdSchema } from "@frontier/content/ship-systems";
import { storedShipFittingSchema } from "@frontier/content/ship-fitting";
import { paintIdSchema, type PaintId } from "@frontier/content/season";
import { frameProfileSchema } from "@frontier/content/hull-frames";
import { stationIdSchema } from "@frontier/content/stations";
import { discoveredLootSchema } from "./discoveries.js";
export { discoveriesViewSchema, type DiscoveriesView } from "./discoveries.js";
import {
  contractBoardSchema,
  contractNpcArchetypeSchema,
  contractTermsSchema,
  missionObjectiveSchema,
  orderIdSchema,
} from "./contract-order-schemas.js";
export { contractOfferSchema } from "./contract-order-schemas.js";
import { z } from "zod";
import { PROTOCOL_VERSION } from "./client-compatibility.js";
import { hullRevisionSchema } from "@frontier/content/supplies";
import { NPC_ARCHETYPES } from "@frontier/content/npcs";
import {
  accountSummarySchema,
  factionIdSchema,
  pilotNameSchema,
} from "./auth-contracts.js";
import {
  escortMissionViewSchema,
  escortProgressSchema,
} from "./escort-schemas.js";
import type { FactionId } from "@frontier/content/factions";
import {
  CONTRACT_IDS,
  PASSIVE_IDS,
  SECTOR_IDS,
  SELECTABLE_CONTRACT_IDS,
} from "@frontier/content/contracts";
import {
  CONTRACT_DIFFICULTIES,
  CUSTOMER_IDS,
  SURVEY_SITE_IDS,
} from "@frontier/content/progression";
import {
  TUTORIAL_MILESTONES,
  TRAINING_MILESTONES,
  type TutorialState,
} from "@frontier/content/tutorial";
import {
  HULL_IDS,
  WEAPON_IDS,
  MODULE_IDS,
} from "@frontier/content/definitions";
import {
  EQUIPMENT,
  type Arsenal,
  type EquipmentKey,
} from "@frontier/content/arsenal";
import type { EquipmentTiers } from "@frontier/content/equipment-tiers";
export type {
  EquipmentTier,
  EquipmentTiers,
} from "@frontier/content/equipment-tiers";
import { NPC_ATTACK_KINDS, type NpcAttackKind } from "@frontier/content/npcs";
import {
  playerPrestigeSchema,
  RELAY_PRESTIGE_ID,
} from "@frontier/content/relay";
import { publicEventPresentationSchema } from "@frontier/content/event-scenarios";
import {
  PUBLIC_EVENT_TYPES,
  type PublicEventResult,
  type PublicEventsView,
  type PublicEventView,
} from "@frontier/content/events";
export type {
  PublicEventView,
  PublicEventResult,
  PublicEventsView,
  EventContribution,
} from "@frontier/content/events";
export type { Arsenal, EquipmentKey } from "@frontier/content/arsenal";
export type {
  RiskPrivateView,
  RiskState,
  CargoLossResult,
  CargoKind,
} from "@frontier/content/risk";
export {
  riskPrivateViewSchema,
  riskStateSchema,
  cargoKindSchema,
  cargoLossResultSchema,
} from "./risk-contracts.js";
export type { ContrabandBeacon } from "./risk-contracts.js";
import type { ContrabandBeacon } from "./risk-contracts.js";
import type {
  ContractResult,
  ContractSelection,
  MissionView,
  RunContractState,
  RunResult,
} from "@frontier/content/contracts";
export type {
  ContractId,
  ContractResult,
  ContractSelection,
  MissionView,
  PassiveId,
  RunContractState,
  RunResult,
  SelectableContractId,
  SectorId,
} from "@frontier/content/contracts";

export { PROTOCOL_VERSION } from "./client-compatibility.js";
export const ROOM_NAME = "frontier";
export const loadoutIdSchema = z.enum(["assault", "defensive"]);
export const hullIdSchema = z.enum(HULL_IDS);
export const weaponIdSchema = z.enum(WEAPON_IDS);
export const moduleIdSchema = z.enum(MODULE_IDS);
export type LoadoutId = z.infer<typeof loadoutIdSchema>;
export type HullId = z.infer<typeof hullIdSchema>;
export type WeaponId = z.infer<typeof weaponIdSchema>;
export type ModuleId = z.infer<typeof moduleIdSchema>;
export const contractIdSchema = z.enum(CONTRACT_IDS);
export const selectableContractIdSchema = z.enum(SELECTABLE_CONTRACT_IDS);
export const contractSelectionSchema = z
  .object({
    orderId: orderIdSchema,
    contractId: selectableContractIdSchema,
    difficulty: z.enum(CONTRACT_DIFFICULTIES),
  })
  .strict() satisfies z.ZodType<ContractSelection>;
export const selectedContractsSchema = z
  .array(contractSelectionSchema)
  .max(3)
  .refine(
    (contracts) =>
      new Set(contracts.map(({ contractId }) => contractId)).size ===
        contracts.length &&
      new Set(contracts.map(({ orderId }) => orderId)).size ===
        contracts.length,
    { message: "Contract selections must have unique contract IDs" },
  );
export const passiveIdSchema = z.enum(PASSIVE_IDS);
export const equipmentTierSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export const equipmentTiersSchema = z
  .object({
    weapon: equipmentTierSchema,
    module: equipmentTierSchema,
    secondaryModule: equipmentTierSchema,
    passive: equipmentTierSchema,
  })
  .strict() satisfies z.ZodType<EquipmentTiers>;
export const equipmentKeySchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("system"), id: shipSystemIdSchema }),
  z.object({ kind: z.literal("weapon"), id: weaponIdSchema }).strict(),
  z.object({ kind: z.literal("module"), id: moduleIdSchema }).strict(),
  z
    .object({
      kind: z.literal("passive"),
      id: passiveIdSchema.exclude(["none"]),
    })
    .strict(),
]) satisfies z.ZodType<EquipmentKey>;
export const arsenalSchema = z
  .object({
    knownBlueprints: z
      .array(equipmentKeySchema)
      .max(EQUIPMENT.length)
      .refine(
        (keys) =>
          new Set(keys.map((key) => `${key.kind}:${key.id}`)).size ===
          keys.length,
      ),
    stacks: z
      .array(
        z
          .object({
            key: equipmentKeySchema,
            tier: equipmentTierSchema,
            count: z.number().int().positive().max(999),
          })
          .strict(),
      )
      .max(EQUIPMENT.length * 3)
      .refine(
        (stacks) =>
          new Set(
            stacks.map(({ key, tier }) => `${key.kind}:${key.id}:${tier}`),
          ).size === stacks.length,
      ),
  })
  .strict() satisfies z.ZodType<Arsenal>;
export const npcAttackKindSchema = z.enum(NPC_ATTACK_KINDS);
export const publicEventTypeSchema = z.enum(PUBLIC_EVENT_TYPES);
const eventContributionSchema = z.object({
  damage: z.number().finite().nonnegative(),
  healing: z.number().finite().nonnegative(),
  tasks: z.number().finite().nonnegative(),
});
export const publicEventResultSchema = z
  .object({
    winnerFaction: factionIdSchema.nullable().exactOptional(),
    terms: publicEventPresentationSchema.nullable().default(null),
    eventId: z.string().min(1).max(100),
    eventType: publicEventTypeSchema,
    outcome: z.enum(["success", "failed"]),
    contribution: eventContributionSchema,
    eligible: z.boolean(),
    credits: z.string(),
    components: z.number().int().nonnegative(),
    blueprintUnlocks: z.array(equipmentKeySchema).max(EQUIPMENT.length),
    resolvedAt: z.number().int().nonnegative(),
  })
  .refine(
    (result) =>
      result.eventType === "relay"
        ? result.terms?.prestigeReward === RELAY_PRESTIGE_ID &&
          (result.outcome === "success"
            ? result.winnerFaction !== undefined &&
              result.winnerFaction !== null
            : result.winnerFaction === null)
        : result.winnerFaction === undefined &&
          result.terms?.prestigeReward === undefined,
    {
      message:
        "Relay results require their frozen reward and outcome's winner; PvE has no winner",
    },
  )
  .refine(result => (result.eventType === "truce") === (result.terms?.version === "public-events-v4"), {
    message: "Truce results require their frozen v4 presentation",
  }) satisfies z.ZodType<PublicEventResult>;
export const publicEventsViewSchema = z.object({
  trackedEventId: z.string().min(1).max(100).nullable(),
  participation: z
    .array(
      eventContributionSchema.extend({
        eventId: z.string().min(1).max(100),
        eligible: z.boolean(),
        pending: z.boolean(),
        interactionProgress: z.number().min(0).max(1),
      }),
    )
    .max(5),
}) satisfies z.ZodType<PublicEventsView>;
const outcomeSchema = z.enum([
  "active",
  "success",
  "partial",
  "diverted",
  "failed",
]);
export const runContractSchema = z
  .object({
    orderId: orderIdSchema,
    terms: contractTermsSchema,
    killedArchetypes: z
      .array(contractNpcArchetypeSchema)
      .max(NPC_ARCHETYPES.length)
      .refine((items) => new Set(items).size === items.length),
    runId: z.string(),
    contractId: contractIdSchema,
    sectorId: z.enum(SECTOR_IDS),
    variant: z.enum(["standard", "ambush", "reinforced"]),
    stage: z.enum(["outbound", "engage", "collect", "return", "settled"]),
    progress: z.number().int().nonnegative(),
    goal: z.number().int().positive(),
    cargoHeld: z.number().int().nonnegative(),
    outcome: outcomeSchema,
    rescue: z.enum(["none", "active", "saved", "lost"]),
    difficulty: z.enum(CONTRACT_DIFFICULTIES),
    eliteKills: z.number().int().nonnegative(),
    surveyedSites: z
      .array(z.enum(SURVEY_SITE_IDS))
      .max(3)
      .refine((sites) => new Set(sites).size === sites.length),
    escort: escortProgressSchema.nullable().default(null),
  })
  .strict()
  .refine(
    (contract) =>
      contract.terms.id === contract.contractId &&
      contract.terms.goal === contract.goal &&
      contract.terms.sectorId === contract.sectorId,
    { message: "Run contract must preserve its frozen terms" },
  )
  .refine(
    (contract) =>
      contract.escort === null ||
      contract.terms.escort?.routeId === contract.escort.routeId,
    { message: "Escort progress must belong to the frozen route" },
  ) satisfies z.ZodType<RunContractState>;
function validContractGroup(
  contracts: readonly Pick<
    RunContractState,
    "runId" | "contractId" | "orderId"
  >[],
): boolean {
  return (
    contracts.length === 0 ||
    (new Set(contracts.map(({ orderId }) => orderId)).size ===
      contracts.length &&
      new Set(contracts.map(({ contractId }) => contractId)).size ===
        contracts.length &&
      new Set(contracts.map(({ runId }) => runId)).size === 1 &&
      (!contracts.some(({ contractId }) => contractId === "intro") ||
        contracts.length === 1))
  );
}
function validRunTotals(result: RunResult): boolean {
  const credits = [
    result.credits,
    result.cargoCredits,
    ...result.contracts.map((contract) => contract.credits),
  ];
  const components = [
    result.components,
    result.cargoComponents,
    ...result.contracts.map((contract) => contract.components),
  ];
  if (
    credits.some((value) => !/^(0|[1-9]\d*)$/.test(value)) ||
    components.some((value) => !Number.isSafeInteger(value) || value < 0)
  )
    return false;
  return (
    BigInt(result.credits) ===
      result.contracts.reduce(
        (sum, contract) => sum + BigInt(contract.credits),
        BigInt(result.cargoCredits),
      ) &&
    BigInt(result.components) ===
      result.contracts.reduce(
        (sum, contract) => sum + BigInt(contract.components),
        BigInt(result.cargoComponents),
      )
  );
}
export const runContractsSchema = z
  .array(runContractSchema)
  .max(3)
  .refine(validContractGroup, {
    message:
      "Contracts must share a run, have unique IDs and keep intro exclusive",
  });
export const missionViewSchema = runContractSchema
  .safeExtend({
    discoveredLoot: discoveredLootSchema,
    title: z.string(),
    objective: missionObjectiveSchema,
    weather: z.enum(["clear", "ion-storm", "debris-field"]),
    rewardCredits: z.string(),
    rewardComponents: z.number(),
    rewardUnlock: passiveIdSchema,
    escortView: escortMissionViewSchema.nullable().default(null),
  })
  .refine(
    (mission) => mission.escortView === null || mission.contractId === "escort",
    {
      message: "Escort runtime belongs exclusively to escort missions",
    },
  ) satisfies z.ZodType<MissionView>;
export const missionsViewSchema = z
  .array(missionViewSchema)
  .max(3)
  .refine(validContractGroup, {
    message:
      "Missions must share a run, have unique IDs and keep intro exclusive",
  });
export const contractResultSchema = z
  .object({
    orderId: orderIdSchema,
    terms: contractTermsSchema,
    runId: z.string(),
    contractId: contractIdSchema,
    outcome: outcomeSchema.exclude(["active"]),
    credits: z.string().regex(/^(0|[1-9]\d*)$/),
    components: z.number().int().nonnegative(),
    equipmentUnlock: passiveIdSchema,
    moduleUnlock: moduleIdSchema.nullable(),
    blueprintUnlocks: z.array(equipmentKeySchema).max(EQUIPMENT.length),
    difficulty: z.enum(CONTRACT_DIFFICULTIES),
    reputationAward: z
      .object({
        customerId: z.enum(CUSTOMER_IDS),
        amount: z.number().int().nonnegative(),
        balance: z.number().int().nonnegative(),
      })
      .nullable(),
  })
  .strict()
  .refine((result) => result.terms.id === result.contractId, {
    message: "Result terms must match the contract family",
  }) satisfies z.ZodType<ContractResult>;
export const runResultSchema = z
  .object({
    runId: z.string(),
    credits: z.string().regex(/^(0|[1-9]\d*)$/),
    components: z.number().int().nonnegative(),
    cargoCredits: z.string().regex(/^(0|[1-9]\d*)$/),
    cargoComponents: z.number().int().nonnegative(),
    contracts: z.array(contractResultSchema).max(3).refine(validContractGroup),
  })
  .strict()
  .refine(
    (result) => result.contracts.every(({ runId }) => runId === result.runId),
    {
      message: "Contract results must belong to the run result",
    },
  )
  .refine(validRunTotals, {
    message: "Run totals must include cargo once and each contract reward once",
  }) satisfies z.ZodType<RunResult>;

export const tutorialStateSchema = z.discriminatedUnion("version", [
  z.object({
    route: z.literal("training").exactOptional(), version: z.literal(1),
    milestones: z.array(z.enum(TUTORIAL_MILESTONES)).max(5)
      .refine(items => new Set(items).size === items.length),
    rewardGranted: z.boolean(), docked: z.boolean(),
  }),
  z.object({
    route: z.literal("training"), version: z.literal(2),
    milestones: z.array(z.enum(TRAINING_MILESTONES)).max(TRAINING_MILESTONES.length)
      .refine(items => new Set(items).size === items.length),
    rewardGranted: z.boolean(), docked: z.boolean(),
  }),
]) satisfies z.ZodType<TutorialState>;

export const profileSchema = z
  .object({
    shipFitting: storedShipFittingSchema.nullable().optional(),
    prestige: playerPrestigeSchema.optional(),
    frame: frameProfileSchema.optional(),
    paintId: paintIdSchema.optional(),
    playerId: z.string(),
    personalLocationId: z.uuid().nullable().optional(),
    name: z.string(),
    nameRevision: z.number().int().nonnegative().optional(),
    factionId: factionIdSchema.nullable(),
    stationId: stationIdSchema.optional(),
    account: accountSummarySchema.nullable(),
    credits: z.string(),
    components: z.number(),
    loadoutId: loadoutIdSchema,
    hullId: hullIdSchema,
    hullRevision: hullRevisionSchema.optional(),
    ownedHulls: z
      .array(hullIdSchema)
      .min(1)
      .max(5)
      .refine((items) => new Set(items).size === items.length),
    arsenal: arsenalSchema,
    weaponId: weaponIdSchema,
    moduleId: moduleIdSchema,
    secondaryModuleId: moduleIdSchema,
    equipmentTiers: equipmentTiersSchema,
    runId: z.string(),
    cargo: z.number(),
    docked: z.boolean(),
    selectedContracts: selectedContractsSchema,
    contractBoard: contractBoardSchema,
    reputation: z.object({
      security: z.number().int().nonnegative(),
      research: z.number().int().nonnegative(),
      logistics: z.number().int().nonnegative(),
    }),
    passiveId: passiveIdSchema,
    unlockedPassives: z.array(passiveIdSchema),
    unlockedModules: z.array(moduleIdSchema),
    tutorial: tutorialStateSchema.nullable(),
    contracts: runContractsSchema,
    lastRunResult: runResultSchema.nullable(),
    lastEventResult: publicEventResultSchema.nullable(),
  })
  .strict()
  .refine(
    (profile) =>
      profile.contracts.every(({ runId }) => runId === profile.runId),
    {
      message: "Active contracts must belong to the profile run",
    },
  );
export type Profile = z.infer<typeof profileSchema>;
export const sessionRequestSchema = z.object({
  token: z.string().optional(),
  name: pilotNameSchema.optional(),
});
export const sessionResponseSchema = z.object({
  token: z.string(),
  profile: profileSchema,
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export const loadoutRequestSchema = z
  .object({
    loadoutId: loadoutIdSchema,
    hullId: hullIdSchema.optional(),
    weaponId: weaponIdSchema.optional(),
    moduleId: moduleIdSchema.optional(),
    secondaryModuleId: moduleIdSchema.optional(),
    passiveId: passiveIdSchema.optional(),
    equipmentTiers: equipmentTiersSchema
      .partial()
      .transform((tiers): Partial<EquipmentTiers> => ({
        ...(tiers.weapon !== undefined ? { weapon: tiers.weapon } : {}),
        ...(tiers.module !== undefined ? { module: tiers.module } : {}),
        ...(tiers.secondaryModule !== undefined
          ? { secondaryModule: tiers.secondaryModule }
          : {}),
        ...(tiers.passive !== undefined ? { passive: tiers.passive } : {}),
      }))
      .optional(),
  })
  .strict();
export const purchaseHullRequestSchema = z
  .object({
    hullId: hullIdSchema,
    requestId: z.string().min(8).max(100),
    priceVersion: z.string().min(1).max(64).optional(),
  })
  .strict();
export const equipmentRequestSchema = z
  .object({
    key: equipmentKeySchema,
    requestId: z.string().min(8).max(100),
  })
  .strict();
export const dismantleRequestSchema = equipmentRequestSchema.extend({
  tier: equipmentTierSchema.default(0),
});
export const contractRequestSchema = z
  .object({ contracts: selectedContractsSchema })
  .strict();
export const upgradePriceVersionSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9.-]*$/);
export const upgradeRequestSchema = z
  .object({
    key: equipmentKeySchema,
    fromTier: equipmentTierSchema,
    requestId: z.string().min(8).max(100),
    priceVersion: upgradePriceVersionSchema.optional(),
    mount: fittingUpgradeTargetSchema.optional(),
  })
  .strict();
export const feedbackRequestSchema = z.object({
  text: z.string().trim().min(1).max(4000),
  rating: z.number().int().min(1).max(5).optional(),
});
export const joinOptionsSchema = z
  .object({
    token: z.string().min(16).max(200),
    locationId: z.uuid().optional(),
    protocolVersion: z.literal(PROTOCOL_VERSION),
  })
  .strict();
export const syncMissionSchema = z.object({}).strict();
export const inputSchema = z
  .object({
    seq: z.number().int().nonnegative(),
    mx: z.number().min(-1).max(1),
    my: z.number().min(-1).max(1),
    targetId: z.string().max(100),
    fire: z.boolean(),
    boost: z.boolean(),
    aimX: z.number().finite(),
    aimY: z.number().finite(),
  })
  .strict();
export type ClientInput = z.infer<typeof inputSchema>;
export const actionSchema = z
  .object({
    requestId: z.string().min(8).max(100),
    type: z.enum([
      "ability",
      "pickup",
      "drop",
      "dock",
      "launch",
      "jump",
      "joinEvent",
      "eventInteract",
      "survey",
    ]),
    slot: z.number().int().min(0).max(10).optional(),
    targetId: z.string().max(100).optional(),
    riskAcknowledged: z.boolean().optional(),
  })
  .strict();
export type ClientAction = z.infer<typeof actionSchema>;
export const noticeSchema = z.object({ message: z.string(), code: z.string() });
export type Notice = z.infer<typeof noticeSchema>;

export const partyMarkerSchema = z.object({
  targetId: z.string().min(1).max(100),
  label: z.string().min(1).max(100),
  x: z.number().finite(),
  y: z.number().finite(),
  kind: z.enum(["combat", "collect", "travel"]),
  authorId: z.string().min(1).max(100),
});
export type PartyMarker = z.infer<typeof partyMarkerSchema>;
export const partyViewSchema = z.object({
  availablePilots: z
    .array(z.object({ playerId: z.string(), name: z.string() }))
    .max(64),
  party: z
    .object({
      id: z.string(),
      leaderId: z.string(),
      members: z
        .array(
          z.object({
            playerId: z.string(),
            name: z.string(),
            online: z.boolean(),
          }),
        )
        .min(1)
        .max(4),
      marker: partyMarkerSchema.nullable(),
    })
    .nullable(),
  invitations: z
    .array(
      z.object({
        partyId: z.string(),
        leaderId: z.string(),
        leaderName: z.string(),
      }),
    )
    .max(8),
});
export type PartyView = z.infer<typeof partyViewSchema>;
export const partyCommandSchema = z.discriminatedUnion("type", [
  z
    .object({ type: z.literal("invite"), targetId: z.string().min(1).max(100) })
    .strict(),
  z
    .object({ type: z.literal("accept"), partyId: z.string().min(1).max(100) })
    .strict(),
  z
    .object({ type: z.literal("decline"), partyId: z.string().min(1).max(100) })
    .strict(),
  z.object({ type: z.literal("leave") }).strict(),
  z
    .object({ type: z.literal("kick"), playerId: z.string().min(1).max(100) })
    .strict(),
  z
    .object({ type: z.literal("mark"), targetId: z.string().min(1).max(100) })
    .strict(),
  z.object({ type: z.literal("clearMarker") }).strict(),
]);
export type PartyCommand = z.infer<typeof partyCommandSchema>;

export interface ShipSnapshot {
  wanted?: boolean;
  securityTargetId?: string;
  paintId?: PaintId;
  lossDisposition?: "keep" | "destroy" | "drop";
  attackGeometry?: import("@frontier/content/combat-rules").NpcAttackGeometry;
  controls?: import("@frontier/content/combat-rules").FlightControls;
  id: string;
  name: string;
  factionId: FactionId | null;
  kind: string;
  archetype: string;
  sectorId: string;
  passiveId: string;
  x: number;
  y: number;
  angle: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  energy: number;
  maxEnergy: number;
  cargo: number;
  energyCargo: number;
  salvageCargo: number;
  contrabandCargo?: number;
  pvpEnabled?: boolean;
  capacity: number;
  docked: boolean;
  dead: boolean;
  pending: boolean;
  loadoutId: string;
  hullId: HullId;
  weaponId: string;
  moduleId: string;
  secondaryModuleId: string;
  equipmentTiers: EquipmentTiers;
  targetId: string;
  abilityCooldown: number;
  secondaryCooldown: number;
  thirdCooldown?: number;
  abilityCooldowns?: number[];
  boosting: boolean;
  radius: number;
  telegraph: number;
  jammingRemaining: number;
  attackKind: NpcAttackKind;
  attackX: number;
  attackY: number;
}
export interface CombatEffectSnapshot {
  id: string;
  kind: "repair-field" | "scan" | "intercept" | "rail-hit";
  ownerId: string;
  x: number;
  y: number;
  radius: number;
  remaining: number;
}
export interface LootSnapshot {
  id: string;
  kind: string;
  x: number;
  y: number;
  value: number;
  pending: boolean;
  claimedBy: string[];
  shared?: boolean;
}
export interface ProjectileSnapshot {
  id: string;
  x: number;
  y: number;
  angle: number;
  kind: string;
  ownerId: string;
}
export interface ObstacleSnapshot {
  resourceHp?: number;
  resourceMaxHp?: number;
  id: string;
  x: number;
  y: number;
  radius: number;
}
export interface GateSnapshot {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  toSectorId: string;
}
export interface RegionSnapshot {
  factionId?: FactionId | null;
  pvpAllowed?: boolean;
  description?: string;
  resourceSummary?: string;
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  weather: string;
  variant: string;
}
export interface WorldSnapshot {
  tick: number;
  time: number;
  event: string;
  eventRemaining: number;
  stationX: number;
  stationY: number;
  width: number;
  height: number;
  players: ShipSnapshot[];
  npcs: ShipSnapshot[];
  loot: LootSnapshot[];
  projectiles: ProjectileSnapshot[];
  combatEffects: CombatEffectSnapshot[];
  publicEvents: PublicEventView[];
  obstacles: ObstacleSnapshot[];
  gates: GateSnapshot[];
  regions: RegionSnapshot[];
  beacons?: ContrabandBeacon[];
}
